import React from "react";
import { Link } from "react-router-dom";
import TableCell from "@material-ui/core/TableCell";
import numberFormatter from "utils/numberFormatter";
import _ from "lodash";
import LoanTableOptionsMenu from "components/Global/OptionsMenu/LoanTable/LoanTableOptionsMenu";
import hedgeTypes from "constants/hedgeTypes";

import { calculateClosingCosts } from "./util";

const MezzLoanTableColumns = [
  {
    id: "690dbce4-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: true,
    dataField: "lenderCompany.name",
    numeric: false,
    label: "Lender",
    Cell: ({ record, callbacks }) => (
      <TableCell>
        {(
          <Link to={`/loan/${callbacks.getDealId()}/${record.id}`}>
            {record["lenderCompany.name"] || "Lender Not Selected"}
          </Link>
        ) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "690dbd0d-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "covenants.ltvStabilized",
    numeric: false,
    label: "LTV",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(_.get(record, "covenants.ltvStabilized"), 2, "\u2014") || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "690dbd1c-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanPricing",
    numeric: false,
    label: "Loan Pricing",
    Cell: ({ record }) => <TableCell>{ _.get(record, "loanPricing") || "\u2014"}</TableCell>
  },
  {
    id: "690dbd29-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "currentRate",
    numeric: false,
    label: "Current Rate",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record.currentRate, 2, "\u2014") }
      </TableCell>
    )
  },
  {
    id: "690dbd38-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prefHedge.hedgeTypeId",
    numeric: false,
    label: "Hedge Type",
    Cell: ({ record }) => {
      if (!_.get(record, "prefHedge.hedgeTypeId")) {
        return <TableCell>{"\u2014"}</TableCell>;
      }

      return (
        <TableCell>
          {
            hedgeTypes.list.filter(x => x.value === _.get(record, "prefHedge.hedgeTypeId"))[0] ?
              hedgeTypes.list.filter(x => x.value === _.get(record, "prefHedge.hedgeTypeId"))[0].name : "Unknown"
          }
        </TableCell>
      );
    }
  },
  {
    id: "690dabc1-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prefHedge.marketCost",
    numeric: false,
    label: "Hedge Premium",
    Cell: ({ record }) => {
      const hedgeType = _.get(record, "prefHedge.hedgeTypeId", "\u2014");
      if (hedgeType === hedgeTypes.CAP) {
        return (<TableCell>{numberFormatter.maskCurrency(_.get(record, "prefHedge.marketCost"), "\u2014", true)}</TableCell>);
      } else if (hedgeType === hedgeTypes.SWAP) {
        return (<TableCell>{numberFormatter.formatRate(_.get(record, "prefHedge.marketRate"), 2, "\u2014") }</TableCell>);
      }

      return (<TableCell>{"\u2014"}</TableCell>);
    }
  },
  {
    id: "690dbd49-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "closingCosts.totalAmount",
    numeric: false,
    label: "Closing Costs",
    Cell: ({ record }) => <TableCell>{numberFormatter.maskCurrency(_.get(record, "closingCosts.totalAmount", "\u2014"))}</TableCell>
  },
  {
    id: "690dbd5a-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "loanRecourse.recourseType.name",
    numeric: false,
    label: "Recourse",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "loanRecourse.recourseType.name") || "\u2014"}</TableCell>
    )
  },
  {
    id: "690dbd6e-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prepaymentTerms.prepaymentType.value",
    numeric: false,
    label: "Prepayment Type",
    Cell: ({ record }) => (
      <TableCell>{_.get(record, "prepaymentTerms.prepaymentType.value") || "\u2014"}</TableCell>
    )
  },
  {
    id: "690dbd83-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "averageProjectedRate",
    numeric: false,
    label: "Projected Rate",
    Cell: ({ record }) => <TableCell>{numberFormatter.formatRate(_.get(record, "averageProjectedRate", null), 2, "\u2014")}</TableCell>
  },
  {
    id: "690dbd9b-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "closingCosts.originationFeePercent",
    numeric: false,
    label: "Origination Fee",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record["closingCosts.originationFeePercent"], 2) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "690dbdb5-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "mezzRateTypeRef.name",
    numeric: false,
    label: "Mezz Rate Type",
    Cell: ({ record }) => (
      <TableCell>{record["mezzRateTypeRef.name"] || "\u2014"}</TableCell>
    )
  },
  {
    id: "690dbe97-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "mezzFundingStructure.name",
    numeric: false,
    label: "Funding Structure",
    Cell: ({ record }) => (
      <TableCell>{record["mezzFundingStructure.name"] || "\u2014"}</TableCell>
    )
  },
  {
    id: "690dbebf-4a55-11e9-aedb-0242ac120002",
    isBaseColumn: false,
    dataField: "prepaymentTerms.exitFee",
    numeric: false,
    label: "Exit Fee",
    Cell: ({ record }) => (
      <TableCell>
        {numberFormatter.formatRate(record["prepaymentTerms.exitFee"], 2) || "\u2014"}
      </TableCell>
    )
  },
  {
    id: "menu",
    isBaseColumn: true,
    dataField: null,
    layoutOrder: 999,
    numeric: false,
    Cell: ({ record, callbacks }) => (
      <TableCell className="loansList_Menu" key="menu">
        <LoanTableOptionsMenu
          id={record.id}
          expirationCallback={callbacks.expired}
          refreshGridCallback={callbacks.refreshGrid}
          tableName={"SubordinateLoanTableColumns"}
        />
      </TableCell>
    )
  }
];

export default MezzLoanTableColumns;
